import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Snackbar,
} from "@mui/material";

// MUI v7 Grid (v2)
import Grid from "@mui/material/Grid";

import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  MedicalServices as MedicalIcon,
  Assignment as AssignmentIcon,
  Comment as CommentIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import PatientAutocomplete from "../../../components/Advanced/Autocomplete/PatientAutocomplete";
import { AuthContext } from "../../../context/AuthContext/AuthContext";

import {
  createReferral as createReferralApi,
  updateReferral as updateReferralApi,
  getReferralById as getReferralByIdApi,
  getSpecialistTypes,
  getCareManagers,
  searchSpecialistsDirectory,
  searchDiagnoses,
  searchHedisCodes,
  searchReferralReasons,
  getPatientDemographics,
  getPatientMemberships,
  getCommentsByReferral,
  createComment as createCommentApi,
  deleteComment as deleteCommentApi,
} from "../services/specialistReferralsService";
import { getStatuses, getLocations } from "../../../utils/services/control/masterDataService";

import {
  LANGUAGES,
  MEMBER_CONDITIONS,
  formatPhoneNumber,
  isValidEmail,
  normalizeICD10,
} from "../constants/referralOptions";

import { debounce } from "../utils/debounce";

// Initial form state — ID-based for API fields
const getInitialFormState = () => ({
  // Patient Information
  cipId: "",
  memberId: "",
  memberFullName: "",
  dob: "",
  memberCondition: "ACTV",
  language: "",
  memberEmail: "",
  memberPhone: "",
  memberMobile: "",
  addressLine1: "",

  // Clinical Information
  urgencyLevelId: "",
  icd10Codes: "",
  reasonForReferral: "",
  hedisCodes: "",
  hasRubiconBeenUsed: false,
  lab: false,
  imaging: false,

  // Specialist Information
  specialistTypeId: "",
  specialistId: null,
  specialistName: "",
  specialistNpi: "",
  specialistPhone: "",
  specialistFax: "",
  specialistAddress: "",

  // Administrative Information
  careManagerId: "",
  pcpId: "",
  pcpName: "",
  locationId: "",
  medicalCenter: "",
  employeeStaffId: null,

  // Status and Follow Up
  referralStatusId: "",
  followupStatusId: "",
  followupNotes: "",
  availityReferral: "",
});

export default function ReferralCreatePage() {
  const navigate = useNavigate();
  const { referralId } = useParams();
  const isEditing = Boolean(referralId);

  const { user } = useContext(AuthContext);

  const [form, setForm] = useState(getInitialFormState());
  const [comments, setComments] = useState([]);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Catalog states (loaded from API)
  const [urgencyLevels, setUrgencyLevels] = useState([]);
  const [referralStatuses, setReferralStatuses] = useState([]);
  const [followupStatuses, setFollowupStatuses] = useState([]);
  const [specialistTypeOptions, setSpecialistTypeOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [careManagerOptions, setCareManagerOptions] = useState([]);

  // Autocomplete search states
  const [diagnosisOptions, setDiagnosisOptions] = useState([]);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [reasonLoading, setReasonLoading] = useState(false);
  const [hedisOptions, setHedisOptions] = useState([]);
  const [hedisLoading, setHedisLoading] = useState(false);
  const [specialistDirOptions, setSpecialistDirOptions] = useState([]);
  const [specialistDirLoading, setSpecialistDirLoading] = useState(false);
  const [selectedSpecialist, setSelectedSpecialist] = useState(null);

  // Accordion expanded states
  const [expanded, setExpanded] = useState({
    patient: true,
    clinical: true,
    specialist: true,
    admin: true,
    status: true,
    comments: false,
  });

  // Debounced search refs
  const diagnosisSearchRef = useRef(null);
  const reasonSearchRef = useRef(null);
  const hedisSearchRef = useRef(null);
  const specialistDirSearchRef = useRef(null);

  // Initialize debounced search functions
  useEffect(() => {
    diagnosisSearchRef.current = debounce(async (term) => {
      if (!term || term.length < 2) { setDiagnosisOptions([]); return; }
      setDiagnosisLoading(true);
      try {
        const results = await searchDiagnoses(term);
        setDiagnosisOptions(results);
      } catch (e) { console.error(e); }
      finally { setDiagnosisLoading(false); }
    }, 300);

    reasonSearchRef.current = debounce(async (term) => {
      if (!term || term.length < 2) { setReasonOptions([]); return; }
      setReasonLoading(true);
      try {
        const results = await searchReferralReasons(term);
        setReasonOptions(results);
      } catch (e) { console.error(e); }
      finally { setReasonLoading(false); }
    }, 300);

    hedisSearchRef.current = debounce(async (term) => {
      if (!term || term.length < 2) { setHedisOptions([]); return; }
      setHedisLoading(true);
      try {
        const results = await searchHedisCodes(term);
        setHedisOptions(results);
      } catch (e) { console.error(e); }
      finally { setHedisLoading(false); }
    }, 300);

    specialistDirSearchRef.current = debounce(async (term, specTypeId) => {
      if (!term || term.length < 2) { setSpecialistDirOptions([]); return; }
      setSpecialistDirLoading(true);
      try {
        const results = await searchSpecialistsDirectory(term, specTypeId || undefined);
        setSpecialistDirOptions(results);
      } catch (e) { console.error(e); }
      finally { setSpecialistDirLoading(false); }
    }, 300);
  }, []);

  // Load catalogs on mount
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [urgency, status, followup, specTypes, locs, cms] = await Promise.all([
          getStatuses("specialist_referral_urgency"),
          getStatuses("specialist_referral_status"),
          getStatuses("specialist_referral_followup"),
          getSpecialistTypes(),
          getLocations({ clinical: true, status: 1 }),
          getCareManagers(),
        ]);
        setUrgencyLevels(urgency || []);
        setReferralStatuses(status || []);
        setFollowupStatuses(followup || []);
        setSpecialistTypeOptions(specTypes || []);
        setLocationOptions(locs || []);
        setCareManagerOptions(cms || []);

        // Set defaults for new referral
        if (!isEditing) {
          // Default urgency to first option
          if (urgency?.length > 0) {
            setForm((prev) => ({ ...prev, urgencyLevelId: prev.urgencyLevelId || urgency[0].statusId }));
          }
          // Default status to first option
          if (status?.length > 0) {
            setForm((prev) => ({ ...prev, referralStatusId: prev.referralStatusId || status[0].statusId }));
          }
          // Default followup to first option
          if (followup?.length > 0) {
            setForm((prev) => ({ ...prev, followupStatusId: prev.followupStatusId || followup[0].statusId }));
          }
          // Set employeeStaffId from logged-in user
          const staffId = user?.employeeId || user?.employee;
          if (staffId) {
            setForm((prev) => ({ ...prev, employeeStaffId: Number(staffId) }));
          }
        }
      } catch (err) {
        console.error("Error loading catalogs:", err);
      }
    };
    loadCatalogs();
  }, [isEditing, user]);

  // Load existing referral for edit mode
  useEffect(() => {
    if (!isEditing || !referralId) return;
    const loadReferral = async () => {
      setLoading(true);
      try {
        const [referral, commentsData] = await Promise.all([
          getReferralByIdApi(Number(referralId)),
          getCommentsByReferral(Number(referralId)),
        ]);
        setForm({
          cipId: referral.cipId || "",
          memberId: referral.memberId || "",
          memberFullName: referral.patientName || "",
          dob: referral.patientDob ? referral.patientDob.split("T")[0] : "",
          memberCondition: "ACTV",
          language: "",
          memberEmail: "",
          memberPhone: referral.patientPhone || "",
          memberMobile: "",
          addressLine1: "",
          urgencyLevelId: referral.urgencyLevelId || "",
          icd10Codes: referral.icd10Codes || "",
          reasonForReferral: referral.reasonForReferral || "",
          hedisCodes: referral.hedisCodes || "",
          hasRubiconBeenUsed: referral.hasRubiconBeenUsed ?? false,
          lab: referral.lab ?? false,
          imaging: referral.imaging ?? false,
          specialistTypeId: referral.specialistTypeId || "",
          specialistId: referral.specialistId || null,
          specialistName: referral.specialistName || "",
          specialistNpi: referral.specialistNpi || "",
          specialistPhone: referral.specialistPhone || "",
          specialistFax: referral.specialistFax || "",
          specialistAddress: referral.specialistAddress || "",
          careManagerId: referral.careManagerId || "",
          pcpId: referral.pcpId || "",
          pcpName: referral.pcpName || "",
          locationId: referral.locationId || "",
          medicalCenter: referral.medicalCenter || "",
          employeeStaffId: referral.employeeStaffId || null,
          referralStatusId: referral.referralStatusId || "",
          followupStatusId: referral.followupStatusId || "",
          followupNotes: referral.followupNotes || "",
          availityReferral: referral.availityReferral ? referral.availityReferral.split("T")[0] : "",
        });
        setComments(commentsData || []);
      } catch (err) {
        console.error("Error loading referral:", err);
        navigate("/specialist-referrals", {
          state: { refresh: true, message: "Error loading referral" },
        });
      } finally {
        setLoading(false);
      }
    };
    loadReferral();
  }, [isEditing, referralId, navigate]);

  const handleChange = useCallback(
    (field, value) => {
      setForm((prev) => {
        const newForm = { ...prev, [field]: value };

        // When location changes, clear PCP and care manager since they depend on location
        if (field === "locationId") {
          const loc = locationOptions?.find((l) => l.locationId === value);
          newForm.medicalCenter = loc?.medicalCenter || "";
        }

        // Normalize ICD-10 to uppercase
        if (field === "icd10Codes") {
          newForm.icd10Codes = normalizeICD10(value);
        }

        return newForm;
      });

      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    },
    [errors, locationOptions]
  );

  const handleToggle = (field, value) => {
    if (value !== null) handleChange(field, value);
  };

  const handleAccordionChange = (panel) => (_event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  // Handle patient selection — fetch demographics + memberships from API
  const handlePatientSelect = async (patient) => {
    if (!patient) {
      setForm((prev) => ({
        ...prev,
        cipId: "",
        memberId: "",
        memberFullName: "",
        dob: "",
        memberCondition: "ACTV",
        memberEmail: "",
        memberPhone: "",
        memberMobile: "",
        addressLine1: "",
        pcpId: "",
        pcpName: "",
        locationId: "",
        medicalCenter: "",
      }));
      return;
    }

    const cipId = patient.cipId || patient.memberId || "";
    setForm((prev) => ({
      ...prev,
      cipId,
      memberFullName: patient.memberFullName || patient.fullName || "",
    }));

    if (!cipId) return;

    setLoadingPatient(true);
    try {
      const [demographics, memberships] = await Promise.all([
        getPatientDemographics(cipId),
        getPatientMemberships(cipId),
      ]);

      setForm((prev) => ({
        ...prev,
        cipId: demographics.cipId || cipId,
        memberFullName: demographics.memberFullName || prev.memberFullName,
        dob: demographics.dob ? demographics.dob.split("T")[0] : "",
        memberEmail: demographics.memberEmail || "",
        memberPhone: demographics.memberPhoneNumber || "",
        memberMobile: demographics.memberMobileNumber || "",
        addressLine1: demographics.addressLine1 || "",
        pcpId: demographics.pcpEmployeeId || "",
        pcpName: demographics.pcpFullName || "",
        locationId: demographics.locationId || "",
        medicalCenter: demographics.medicalCenter || "",
      }));

      // Pick first active membership for memberId
      if (memberships?.length > 0) {
        setForm((prev) => ({ ...prev, memberId: memberships[0].memberId || "" }));
      }
    } catch (err) {
      console.error("Error fetching patient details:", err);
    } finally {
      setLoadingPatient(false);
    }
  };

  // Specialist directory selection
  const handleSpecialistSelect = (_event, specialist) => {
    setSelectedSpecialist(specialist);
    if (specialist) {
      handleChange("specialistId", specialist.specialistId);
      handleChange("specialistName", specialist.specialistName || "");
      handleChange("specialistNpi", specialist.npi || "");
      handleChange("specialistPhone", specialist.phone || "");
      handleChange("specialistFax", specialist.fax || "");
      handleChange("specialistAddress",
        [specialist.address, specialist.city, specialist.state, specialist.zipCode]
          .filter(Boolean)
          .join(", ")
      );
      if (specialist.specialistTypeId) {
        handleChange("specialistTypeId", specialist.specialistTypeId);
      }
    }
  };

  // HEDIS multi-select
  const handleHedisChange = (_event, newValue) => {
    const codes = newValue
      .map((v) => (typeof v === "string" ? v : v.hedisCode))
      .filter(Boolean);
    handleChange("hedisCodes", codes.join(","));
  };

  // Comments — Add via API (only in edit mode)
  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    if (isEditing && referralId) {
      try {
        await createCommentApi({
          specialistReferralId: Number(referralId),
          comment: newComment,
        });
        // Refresh comments
        const updated = await getCommentsByReferral(Number(referralId));
        setComments(updated || []);
      } catch (err) {
        console.error("Error adding comment:", err);
        setSnackbar({ open: true, message: "Error adding comment", severity: "error" });
      }
    } else {
      // For new referral, store locally (will be lost if not saved)
      setComments((prev) => [
        ...prev,
        {
          commentId: `local-${Date.now()}`,
          comment: newComment,
          createdBy: user?.name || "Staff",
          createDate: new Date().toISOString(),
        },
      ]);
    }

    setNewComment("");
    setCommentDialogOpen(false);
  };

  // Delete comment via API
  const handleDeleteComment = async (commentId) => {
    if (typeof commentId === "string" && commentId.startsWith("local-")) {
      setComments((prev) => prev.filter((c) => c.commentId !== commentId));
      return;
    }
    try {
      await deleteCommentApi(commentId);
      setComments((prev) => prev.filter((c) => c.commentId !== commentId));
    } catch (err) {
      console.error("Error deleting comment:", err);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!form.cipId?.trim()) newErrors.cipId = "Patient is required";
    if (!form.memberFullName?.trim()) newErrors.memberFullName = "Member name is required";
    if (!form.urgencyLevelId) newErrors.urgencyLevelId = "Urgency level is required";
    if (!form.icd10Codes?.trim()) newErrors.icd10Codes = "ICD-10 code is required";
    if (!form.reasonForReferral?.trim()) newErrors.reasonForReferral = "Reason for referral is required";
    if (!form.specialistTypeId) newErrors.specialistTypeId = "Specialist type is required";
    if (!form.pcpId) newErrors.pcpId = "Provider is required";
    if (!form.locationId) newErrors.locationId = "Center is required";
    if (!form.referralStatusId) newErrors.referralStatusId = "Status is required";

    if (form.memberEmail && !isValidEmail(form.memberEmail)) {
      newErrors.memberEmail = "Invalid email format";
    }

    setErrors(newErrors);
    return { ok: Object.keys(newErrors).length === 0, newErrors };
  };

  // Save handler — API
  const handleSave = async () => {
    const { ok, newErrors } = validateForm();
    if (!ok) {
      // Expand sections with errors
      const sectionsWithErrors = {
        patient: ["cipId", "memberFullName", "memberEmail"].some((f) => !!newErrors[f]),
        clinical: ["urgencyLevelId", "icd10Codes", "reasonForReferral"].some((f) => !!newErrors[f]),
        specialist: ["specialistTypeId"].some((f) => !!newErrors[f]),
        admin: ["pcpId", "locationId"].some((f) => !!newErrors[f]),
        status: ["referralStatusId"].some((f) => !!newErrors[f]),
      };
      setExpanded((prev) => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(sectionsWithErrors)
            .filter(([, v]) => v)
            .map(([k]) => [k, true])
        ),
      }));
      setSnackbar({ open: true, message: "Please fix the required fields.", severity: "error" });
      return;
    }

    setLoading(true);
    try {
      const toInt = (val) => (val ? Number(val) : null);

      const payload = {
        cipId: form.cipId,
        memberId: form.memberId || null,
        urgencyLevelId: toInt(form.urgencyLevelId),
        icd10Codes: form.icd10Codes || null,
        reasonForReferral: form.reasonForReferral || null,
        hedisCodes: form.hedisCodes || null,
        hasRubiconBeenUsed: form.hasRubiconBeenUsed ?? false,
        lab: form.lab ?? false,
        imaging: form.imaging ?? false,
        specialistTypeId: toInt(form.specialistTypeId),
        specialistId: toInt(form.specialistId),
        specialistName: form.specialistName || null,
        specialistNpi: form.specialistNpi || null,
        specialistPhone: form.specialistPhone || null,
        specialistFax: form.specialistFax || null,
        specialistAddress: form.specialistAddress || null,
        careManagerId: toInt(form.careManagerId),
        pcpId: toInt(form.pcpId),
        locationId: toInt(form.locationId),
        employeeStaffId: toInt(form.employeeStaffId) || toInt(user?.employeeId || user?.employee),
        referralStatusId: toInt(form.referralStatusId),
        followupStatusId: toInt(form.followupStatusId),
        followupNotes: form.followupNotes || null,
        availityReferral: form.availityReferral || null,
      };

      console.log("[SR] createReferral payload:", JSON.stringify(payload, null, 2));

      if (isEditing) {
        payload.specialistReferralId = Number(referralId);
        await updateReferralApi(payload);
      } else {
        await createReferralApi(payload);
      }

      navigate("/specialist-referrals", {
        state: {
          refresh: true,
          message: isEditing ? "Referral updated successfully" : "Referral created successfully",
        },
      });
    } catch (err) {
      console.error("Error saving referral:", err);
      const errorMsg = err?.response?.data?.message || "Error saving referral";
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate("/specialist-referrals");

  // Parse hedis codes string to array for Autocomplete value
  const hedisCodesArray = form.hedisCodes
    ? form.hedisCodes.split(",").map((c) => c.trim()).filter(Boolean)
    : [];

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={handleCancel} size="large">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {isEditing ? "Edit Referral" : "New Specialist Referral"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditing ? "Update referral information" : "Create a new referral to a specialist"}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={handleCancel} size="large">
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={loading}
            size="large"
          >
            {isEditing ? "Update" : "Save"} Referral
          </Button>
        </Stack>
      </Box>

      {/* Two column layout */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid size={{ xs: 12, lg: 6 }}>
          {/* SECTION 1: Patient Information */}
          <Accordion expanded={expanded.patient} onChange={handleAccordionChange("patient")} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Patient Information
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <PatientAutocomplete
                    label="Search Patient *"
                    placeholder="Type patient name to search..."
                    error={!!errors.memberFullName || !!errors.cipId}
                    helperText={errors.memberFullName || errors.cipId}
                    value={
                      form.cipId && form.memberFullName
                        ? { memberId: form.cipId, memberFullName: form.memberFullName }
                        : null
                    }
                    onPatientSelect={handlePatientSelect}
                  />

                  {loadingPatient && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        Loading patient details...
                      </Typography>
                    </Stack>
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="CIP ID"
                    value={form.cipId}
                    disabled
                    InputProps={{ readOnly: true }}
                    helperText="Auto-filled from patient selection"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Member ID"
                    value={form.memberId}
                    disabled
                    InputProps={{ readOnly: true }}
                    helperText="Auto-filled from memberships"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date of Birth"
                    value={form.dob}
                    onChange={(e) => handleChange("dob", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={loadingPatient}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Member Condition"
                    value={form.memberCondition}
                    onChange={(e) => handleChange("memberCondition", e.target.value)}
                    disabled={loadingPatient}
                  >
                    {MEMBER_CONDITIONS.map((cond) => (
                      <MenuItem key={cond.value} value={cond.value}>
                        {cond.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Primary Language"
                    value={form.language}
                    onChange={(e) => handleChange("language", e.target.value)}
                  >
                    <MenuItem value="">Select Language...</MenuItem>
                    {LANGUAGES.map((lang) => (
                      <MenuItem key={lang.value} value={lang.value}>
                        {lang.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={form.memberEmail}
                    onChange={(e) => handleChange("memberEmail", e.target.value)}
                    error={!!errors.memberEmail}
                    helperText={errors.memberEmail}
                    disabled={loadingPatient}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={form.memberPhone}
                    onChange={(e) => handleChange("memberPhone", formatPhoneNumber(e.target.value))}
                    placeholder="(000) 000-0000"
                    disabled={loadingPatient}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    value={form.memberMobile}
                    onChange={(e) => handleChange("memberMobile", formatPhoneNumber(e.target.value))}
                    placeholder="(000) 000-0000"
                    disabled={loadingPatient}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={form.addressLine1}
                    onChange={(e) => handleChange("addressLine1", e.target.value)}
                    disabled={loadingPatient}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* SECTION 2: Clinical Information */}
          <Accordion expanded={expanded.clinical} onChange={handleAccordionChange("clinical")} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <MedicalIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Clinical Information
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    required
                    label="Urgency Level"
                    value={form.urgencyLevelId}
                    onChange={(e) => handleChange("urgencyLevelId", e.target.value)}
                    error={!!errors.urgencyLevelId}
                    helperText={errors.urgencyLevelId}
                  >
                    <MenuItem value="">Select Urgency...</MenuItem>
                    {urgencyLevels.map((level) => (
                      <MenuItem key={level.statusId} value={level.statusId}>
                        {level.statusName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Autocomplete
                    freeSolo
                    options={diagnosisOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : `${option.code} - ${option.description}`
                    }
                    inputValue={form.icd10Codes}
                    onInputChange={(_e, value) => {
                      handleChange("icd10Codes", normalizeICD10(value || ""));
                      diagnosisSearchRef.current?.(value);
                    }}
                    onChange={(_e, value) => {
                      if (typeof value === "string") {
                        handleChange("icd10Codes", normalizeICD10(value));
                      } else if (value) {
                        // Append code to existing codes
                        const existing = form.icd10Codes ? form.icd10Codes.split(",").map((c) => c.trim()) : [];
                        if (!existing.includes(value.code)) {
                          existing.push(value.code);
                        }
                        handleChange("icd10Codes", existing.join(","));
                      }
                    }}
                    loading={diagnosisLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        fullWidth
                        label="ICD-10 Code"
                        error={!!errors.icd10Codes}
                        helperText={errors.icd10Codes || "Multiple codes separated by comma"}
                        placeholder="e.g., I10, E11.9"
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    freeSolo
                    options={reasonOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : option.referralReason
                    }
                    inputValue={form.reasonForReferral}
                    onInputChange={(_e, value) => {
                      handleChange("reasonForReferral", value || "");
                      reasonSearchRef.current?.(value);
                    }}
                    onChange={(_e, value) => {
                      if (typeof value === "string") {
                        handleChange("reasonForReferral", value);
                      } else if (value) {
                        handleChange("reasonForReferral", value.referralReason);
                      }
                    }}
                    loading={reasonLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        fullWidth
                        label="Reason for Referral"
                        error={!!errors.reasonForReferral}
                        helperText={errors.reasonForReferral}
                        multiline
                        rows={2}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.referralReasonId}>
                        <Box>
                          <Typography variant="body2">{option.referralReason}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Used {option.timesUsed} times
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={hedisOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : `${option.hedisCode} - ${option.hedisDescription}`
                    }
                    value={hedisCodesArray}
                    onInputChange={(_e, value) => {
                      hedisSearchRef.current?.(value);
                    }}
                    onChange={handleHedisChange}
                    loading={hedisLoading}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth label="HEDIS" placeholder="Search HEDIS codes..." />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={typeof option === "string" ? option : option.hedisCode}
                          size="small"
                          color="secondary"
                          {...getTagProps({ index })}
                          key={`hedis-${index}`}
                        />
                      ))
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Virtual Consult
                  </Typography>
                  <ToggleButtonGroup
                    value={form.hasRubiconBeenUsed}
                    exclusive
                    onChange={(_e, v) => handleToggle("hasRubiconBeenUsed", v)}
                    fullWidth
                  >
                    <ToggleButton value={true} color="success">
                      Yes
                    </ToggleButton>
                    <ToggleButton value={false} color="error">
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Lab Required
                  </Typography>
                  <ToggleButtonGroup
                    value={form.lab}
                    exclusive
                    onChange={(_e, v) => handleToggle("lab", v)}
                    fullWidth
                  >
                    <ToggleButton value={true} color="success">
                      Yes
                    </ToggleButton>
                    <ToggleButton value={false} color="error">
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Imaging Required
                  </Typography>
                  <ToggleButtonGroup
                    value={form.imaging}
                    exclusive
                    onChange={(_e, v) => handleToggle("imaging", v)}
                    fullWidth
                  >
                    <ToggleButton value={true} color="success">
                      Yes
                    </ToggleButton>
                    <ToggleButton value={false} color="error">
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* SECTION 4: Administrative Information */}
          <Accordion expanded={expanded.admin} onChange={handleAccordionChange("admin")} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AssignmentIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Administrative Information
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Autocomplete
                    options={careManagerOptions}
                    getOptionLabel={(option) => option?.fullName || ""}
                    value={careManagerOptions.find((cm) => cm.employeeId === form.careManagerId) || null}
                    onChange={(_e, cm) => {
                      handleChange("careManagerId", cm ? cm.employeeId : "");
                    }}
                    isOptionEqualToValue={(option, value) => option.employeeId === value?.employeeId}
                    fullWidth
                    renderInput={(params) => (
                      <TextField {...params} label="Care Manager" fullWidth />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.employeeId}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {option.fullName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.medicalCenter || option.title || ""}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Provider Name (PCP)"
                    value={form.pcpName || ""}
                    disabled
                    InputProps={{ readOnly: true }}
                    error={!!errors.pcpId}
                    helperText={errors.pcpId || "Auto-filled from patient selection"}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    required
                    label="Center"
                    value={form.locationId}
                    onChange={(e) => handleChange("locationId", e.target.value)}
                    error={!!errors.locationId}
                    helperText={errors.locationId}
                  >
                    <MenuItem value="">Select Center...</MenuItem>
                    {locationOptions.map((loc) => (
                      <MenuItem key={loc.locationId} value={loc.locationId}>
                        {loc.medicalCenter}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Staff: <strong>{user?.name || "N/A"}</strong> (auto-assigned)
                  </Alert>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* SECTION 5: Status & Follow Up */}
          <Accordion expanded={expanded.status} onChange={handleAccordionChange("status")}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <HospitalIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Status & Follow Up
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    select
                    required
                    label="Status"
                    value={form.referralStatusId}
                    onChange={(e) => handleChange("referralStatusId", e.target.value)}
                    error={!!errors.referralStatusId}
                    helperText={errors.referralStatusId}
                  >
                    {referralStatuses.map((s) => (
                      <MenuItem key={s.statusId} value={s.statusId}>
                        {s.statusName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    select
                    label="Follow Up"
                    value={form.followupStatusId}
                    onChange={(e) => handleChange("followupStatusId", e.target.value)}
                  >
                    <MenuItem value="">Select Follow Up...</MenuItem>
                    {followupStatuses.map((s) => (
                      <MenuItem key={s.statusId} value={s.statusId}>
                        {s.statusName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date of Availity Referral"
                    value={form.availityReferral}
                    onChange={(e) => handleChange("availityReferral", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Follow Up Notes"
                    value={form.followupNotes}
                    onChange={(e) => handleChange("followupNotes", e.target.value)}
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Right Column */}
        <Grid size={{ xs: 12, lg: 6 }}>
          {/* Specialist Search from Directory */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2, bgcolor: "primary.50" }}>
            <Typography variant="h6" fontWeight={700} color="primary" sx={{ mb: 2 }}>
              <SearchIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Search Specialist from Directory
            </Typography>

            <Grid container spacing={2} alignItems="flex-start">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  select
                  label="Filter by Specialty"
                  value={form.specialistTypeId}
                  onChange={(e) => handleChange("specialistTypeId", e.target.value)}
                  error={!!errors.specialistTypeId}
                  helperText={errors.specialistTypeId}
                >
                  <MenuItem value="">All Specialties</MenuItem>
                  {specialistTypeOptions.map((spec) => (
                    <MenuItem key={spec.specialistTypeId} value={spec.specialistTypeId}>
                      {spec.specialistTypeName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Autocomplete
                  fullWidth
                  options={specialistDirOptions}
                  value={selectedSpecialist}
                  onChange={handleSpecialistSelect}
                  onInputChange={(_e, value) => {
                    specialistDirSearchRef.current?.(value, form.specialistTypeId);
                  }}
                  getOptionLabel={(option) => option?.specialistName || ""}
                  isOptionEqualToValue={(option, value) => option.specialistId === value?.specialistId}
                  noOptionsText="Type to search specialists..."
                  loading={specialistDirLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Search by name, NPI, or location"
                      placeholder="Type to search specialists..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.specialistId}>
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight={700}>
                          {option.specialistName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.specialistTypeName}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {[option.city, option.state].filter(Boolean).join(", ")}
                          {option.npi ? ` | NPI: ${option.npi}` : ""}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Grid>

              {selectedSpecialist && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="success" sx={{ py: 0.75 }}>
                    <Typography variant="body2" fontWeight={700}>
                      Selected: {selectedSpecialist.specialistName}
                    </Typography>
                    <Typography variant="caption">
                      {selectedSpecialist.specialistTypeName} | {selectedSpecialist.city}, {selectedSpecialist.state}
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* SECTION 3: Specialist Information */}
          <Accordion expanded={expanded.specialist} onChange={handleAccordionChange("specialist")} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <HospitalIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Specialist Information
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    select
                    required
                    label="Specialist Type"
                    value={form.specialistTypeId}
                    onChange={(e) => handleChange("specialistTypeId", e.target.value)}
                    error={!!errors.specialistTypeId}
                    helperText={errors.specialistTypeId}
                  >
                    <MenuItem value="">Select Type...</MenuItem>
                    {specialistTypeOptions.map((spec) => (
                      <MenuItem key={spec.specialistTypeId} value={spec.specialistTypeId}>
                        {spec.specialistTypeName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Specialist's Name"
                    value={form.specialistName}
                    onChange={(e) => handleChange("specialistName", e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="NPI"
                    value={form.specialistNpi}
                    onChange={(e) => handleChange("specialistNpi", e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Specialist's Address"
                    value={form.specialistAddress}
                    onChange={(e) => handleChange("specialistAddress", e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone #"
                    value={form.specialistPhone}
                    onChange={(e) => handleChange("specialistPhone", formatPhoneNumber(e.target.value))}
                    placeholder="(000) 000-0000"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Fax #"
                    value={form.specialistFax}
                    onChange={(e) => handleChange("specialistFax", formatPhoneNumber(e.target.value))}
                    placeholder="(000) 000-0000"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Comments Section */}
          <Accordion expanded={expanded.comments} onChange={handleAccordionChange("comments")}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CommentIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Comments ({comments.length})
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setCommentDialogOpen(true)}
                  size="small"
                >
                  Add Comment
                </Button>
              </Box>

              {comments.length === 0 ? (
                <Alert severity="info">No comments yet. Click "Add Comment" to add one.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Comment</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Staff</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comments.map((c) => (
                      <TableRow key={c.commentId}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {new Date(c.createDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{c.comment}</TableCell>
                        <TableCell>{c.createdBy}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteComment(c.commentId)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* Add Comment Dialog */}
      <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Enter your comment..."
            inputProps={{ maxLength: 2000 }}
            helperText={`${newComment.length}/2000 characters`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddComment} disabled={!newComment.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
